import React, { useEffect, useRef, useState } from 'react'
import {
    ArrowRight,
    ChevronDown,
    FileText,
    Link2,
    Check,
    X,
    Users,
    Lock,
    Clock,
    MessageCircle,
    Ban,
    // Volume2,
    // VolumeX,
    // Play,
    // RotateCcw,
} from 'lucide-react'
import { Pages } from '../../Pages'
import { ensureJsAttemptId, jsCapture } from '../../jobseeker/jobseekerPosthog'
import CandidateSignUpForm from './components/CandidateSignUpForm'
import LinkedInLink from '@/components/LinkedInLink'
import authApi from '@/api/auth/AuthApi'
import {
    speakToKitSerifStyle,
    speakToKitThemeVars,
} from './theme/speakToKitThemeVars'
import SpeakToKitLogo from './components/SpeakToKitLogo'
import useSpeakToKitPageTitle from './hooks/useSpeakToKitPageTitle'

const ELEVENLABS_EMBED_URL = 'REPLACE_ME'
const PROFILE_PREVIEW_URL = '/Salma.pdf'
const NETWORK_PREVIEW_IMG = '/network-preview.png'
/* Video configuration preserved for future reinstatement.
const VIDEO_URL =
    'https://s3.eu-west-1.amazonaws.com/contxt.public/network_kit_intro_video_1.mp4'
const VIDEO_START_TIME_SECONDS = 7
const VIDEO_END_TIME_SECONDS = 21
*/

type LandingCtaProps = {
    onClick: () => void
    className?: string
    buttonClassName?: string
    text?: string
    subtext?: string
    showSubtext?: boolean
    variant?: 'message' | 'arrow'
}

const testimonials = [
    {
        quote: "It forces you to reflect on your career in a way that's genuinely useful.",
        author: 'Haig',
        role: 'Investment Analyst',
    },
    {
        quote: "I'd recommend this to other colleagues looking for work.",
        author: 'Ahmed',
        role: 'HR Professional',
    },
    {
        quote: 'I found it clear and thoughtful, and appreciated the focus on teams and working environments rather than just roles.',
        author: 'Ludovico',
        role: 'Hedgefund Analyst',
    },
]

const coldStats = [
    { value: '70-85%', label: "of jobs are filled before they're ever posted" },
    { value: '250', label: "CVs land on a recruiter's desk for every role" },
    { value: '0.1%', label: 'chance of getting hired when you apply cold' },
    { value: '2%', label: 'of cold applicants even get an interview' },
]

const networkStats = [
    { value: '30%', label: 'success rate when you have a connection path' },
    { value: '4-7x', label: 'more likely to be hired than cold applicants' },
    { value: '40%', label: 'of all hires from just 7% of referred applicants' },
    {
        value: '45%',
        label: 'of referred hires stay 4+ years vs 25% from job boards',
    },
]

const whatYouGetCards = [
    {
        title: 'Your Team Match Profile',
        subtitle: 'Understand where you do your best work',
        icon: FileText,
        footer: 'Every caller receives this — no names required.',
        features: [
            'The environments where you thrive',
            'The management styles that unlock you',
            'Questions to ask in interviews to spot the right teams',
            'Delivered via email within 24 hours',
        ],
    },
    {
        title: 'Network-Matched Jobs',
        subtitle: 'Get matched to teams, not keywords',
        icon: Link2,
        badge: 'coming soon!',
        footer: "Unlocked when you share who you've worked well with.",
        features: [
            'See your referral network in full',
            'Monitored for new openings in real-time',
            'Connection paths shown before you apply',
            'You decide whether to reach out — no spam',
        ],
    },
]

const trustItems = [
    {
        icon: Lock,
        title: 'Confidential',
        description: 'We never contact anyone without your permission',
    },
    { icon: Clock, title: '8 minutes', description: 'No prep needed' },
    {
        icon: MessageCircle,
        title: 'No interview',
        description:
            "Just a conversation about where you've done your best work",
    },
    {
        icon: Ban,
        title: 'No spam',
        description: 'You control what happens next',
    },
]

const containerClass = 'mx-auto w-full max-w-[1200px] px-6 font-sans'

function LandingCta({
    onClick,
    className = '',
    buttonClassName = '',
    text = 'Speak to Kit',
    subtext = '8 minutes · Confidential · Free',
    showSubtext = true,
    variant = 'message',
}: LandingCtaProps) {
    const containerClasses =
        `flex flex-col items-center space-y-3 pt-2 ${className}`.trim()
    const buttonClasses =
        `inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base md:text-lg font-medium rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${buttonClassName}`.trim()

    const leadingIcon =
        variant === 'message' ? <MessageCircle className="w-4 h-4" /> : null

    const trailingIcon =
        variant === 'arrow' ? <ArrowRight className="w-4 h-4" /> : null

    return (
        <div className={containerClasses}>
            <button type="button" className={buttonClasses} onClick={onClick}>
                {leadingIcon}
                {text}
                {trailingIcon}
            </button>
            {showSubtext && (
                <p className="text-sm text-muted-foreground text-center">
                    {subtext}
                </p>
            )}
        </div>
    )
}

type ModalType = 'profile' | 'network' | null

type VoiceCardProps = {
    onCtaClick: () => void
    ctaText: string
    signupForm?: React.ReactNode
}

function VoiceCard({ onCtaClick, ctaText, signupForm }: VoiceCardProps) {
    const hasEmbed = ELEVENLABS_EMBED_URL !== 'REPLACE_ME'
    const isSpeakToKitCta = ctaText.trim().toLowerCase() === 'speak to kit'
    const heroButtonClassName = `${
        isSpeakToKitCta ? 'px-8 md:px-10' : 'w-full'
    } group-hover:scale-105 transition-transform duration-300`
    const mobileOverlayRef = useRef<HTMLDivElement | null>(null)
    const [isMobileOverlayExpanded, setIsMobileOverlayExpanded] =
        useState(false)

    const renderVoiceOverlayContent = (
        variant: 'desktop' | 'mobile' = 'desktop',
    ) => (
        <div
            className={`space-y-3 leading-relaxed text-white ${
                variant === 'mobile'
                    ? 'text-xs sm:text-sm'
                    : 'text-sm md:text-base'
            }`}
        >
            <p>Hi, I&apos;m Dan, CEO and line manager of Kit.</p>
            <p>
                Kit is a voice agent that will deep-search the hiring market and
                match you to jobs - all while you&apos;re in conversation. This
                is the result of 25 years of recruitment experience combined
                with the latest advances in AI.
            </p>
            <div className="flex items-center gap-2">
                <p className="flex-1">
                    Speak to Kit and let me know how you get on.
                </p>
                <LinkedInLink
                    url="https://www.linkedin.com/in/danielhalstead/"
                    ariaLabel="Connect with me on LinkedIn"
                    className="inline-flex shrink-0"
                    iconClassName="text-white text-xl"
                />
            </div>
        </div>
    )

    /* Video logic preserved for future reinstatement.
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const [isMuted, setIsMuted] = useState(true)
    const [isVideoPlaying, setIsVideoPlaying] = useState(false)

    useEffect(() => {
        const video = videoRef.current
        if (!video) {
            return
        }

        const handleLoadedMetadata = () => {
            if (video.currentTime < VIDEO_START_TIME_SECONDS) {
                video.currentTime = VIDEO_START_TIME_SECONDS
            }

            video.muted = true
            video.play().catch(() => null)
        }

        video.addEventListener('loadedmetadata', handleLoadedMetadata)

        if (video.readyState >= 1) {
            handleLoadedMetadata()
        }

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
        }
    }, [hasEmbed, isMobileOverlayExpanded])

    useEffect(() => {
        const video = videoRef.current
        if (!video) {
            return
        }

        const handlePlayEvent = () => setIsVideoPlaying(true)
        const handlePauseEvent = () => setIsVideoPlaying(false)
        const handleEndedEvent = () => {
            setIsVideoPlaying(false)
            video.currentTime = VIDEO_START_TIME_SECONDS
        }
        const handleTimeUpdate = () => {
            if (video.currentTime >= VIDEO_END_TIME_SECONDS) {
                video.pause()
                setIsVideoPlaying(false)
                video.currentTime = VIDEO_START_TIME_SECONDS
            }
        }

        video.addEventListener('play', handlePlayEvent)
        video.addEventListener('pause', handlePauseEvent)
        video.addEventListener('ended', handleEndedEvent)
        video.addEventListener('timeupdate', handleTimeUpdate)

        if (!video.paused && !video.ended) {
            setIsVideoPlaying(true)
        }

        return () => {
            video.removeEventListener('play', handlePlayEvent)
            video.removeEventListener('pause', handlePauseEvent)
            video.removeEventListener('ended', handleEndedEvent)
            video.removeEventListener('timeupdate', handleTimeUpdate)
        }
    }, [])

    const toggleMute = () => {
        const next = !isMuted
        setIsMuted(next)
        if (videoRef.current) {
            videoRef.current.muted = next
            if (!next && videoRef.current.paused) {
                videoRef.current.play().catch(() => null)
            }
        }
    }

    const handleRestartClick = () => {
        const video = videoRef.current
        if (!video) {
            return
        }
        video.currentTime = VIDEO_START_TIME_SECONDS
        video.play().catch(() => null)
    }

    const handlePlayClick = () => {
        const video = videoRef.current
        if (!video) {
            return
        }
        video.play().catch(() => null)
    }
    */

    const mobileOverlayPositionClasses = isMobileOverlayExpanded
        ? 'top-3 sm:top-4 bottom-3 sm:bottom-4'
        : 'top-[70%] bottom-3 sm:bottom-4'

    const handleMobileOverlayToggle = () => {
        setIsMobileOverlayExpanded((prev) => {
            const next = !prev
            if (next) {
                mobileOverlayRef.current?.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                })
            }
            return next
        })
    }

    if (hasEmbed) {
        return (
            <div className="card-voice">
                <div className="aspect-square w-full rounded-xl overflow-hidden bg-secondary">
                    <iframe
                        src={ELEVENLABS_EMBED_URL}
                        className="w-full h-full border-0"
                        allow="microphone"
                        title="Speak to Kit"
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="card-voice group">
            <div className="px-8 pt-0 pb-12 flex flex-col items-center gap-8">
                {/* Video temporarily replaced with static hero image — keeping original markup for future use.
                <div className="relative w-full aspect-[4/3] rounded-[32px] bg-gradient-to-br from-primary to-primary/80 overflow-hidden shadow-lg shadow-primary/30">
                    <video
                        ref={videoRef}
                        src={VIDEO_URL}
                        autoPlay
                        muted={isMuted}
                        playsInline
                        className="h-full w-full object-cover"
                        aria-hidden="true"
                    />
                    {!isVideoPlaying ? (
                        <button
                            type="button"
                            onClick={handlePlayClick}
                            className="absolute bottom-4 left-4 inline-flex items-center justify-center rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            aria-label="Play video"
                        >
                            <Play className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleRestartClick}
                            className="absolute bottom-4 left-4 inline-flex items-center justify-center rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            aria-label="Restart video"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={toggleMute}
                        className="absolute bottom-4 right-4 inline-flex items-center justify-center rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                        aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                    >
                        {isMuted ? (
                            <VolumeX className="w-5 h-5" />
                        ) : (
                            <Volume2 className="w-5 h-5" />
                        )}
                    </button>
                </div>
                */}
                <div className="relative w-full aspect-[4/3] rounded-[32px] bg-gradient-to-br from-primary to-primary/80 overflow-hidden shadow-lg shadow-primary/30">
                    <img
                        src="/Dan.png"
                        alt="Dan Halstead"
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                    <div className="hidden lg:block absolute inset-x-4 bottom-4 rounded-2xl bg-black/70 text-white p-4 md:p-5 backdrop-blur-sm shadow-lg max-h-[calc(100%-2rem)] overflow-y-auto">
                        {renderVoiceOverlayContent()}
                    </div>
                    <div
                        className={`lg:hidden absolute inset-x-3 ${mobileOverlayPositionClasses} transition-all duration-300 ease-out`}
                    >
                        <div className="h-full rounded-2xl bg-black/80 text-white p-3 sm:p-4 backdrop-blur-sm shadow-lg border border-white/10 relative">
                            <button
                                type="button"
                                className="absolute left-1/2 -top-3 -translate-x-1/2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/80 border border-white/30 text-white shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                aria-label={
                                    isMobileOverlayExpanded
                                        ? 'Collapse text panel'
                                        : 'Expand text panel'
                                }
                                aria-expanded={isMobileOverlayExpanded}
                                onClick={handleMobileOverlayToggle}
                            >
                                <ChevronDown
                                    className={`w-3 h-3 transition-transform ${
                                        isMobileOverlayExpanded
                                            ? ''
                                            : 'rotate-180'
                                    }`}
                                />
                            </button>
                            <div
                                ref={mobileOverlayRef}
                                className="h-full overflow-y-auto pr-1 pt-2 sm:pt-3"
                                style={{ scrollbarWidth: 'none' }}
                            >
                                {renderVoiceOverlayContent('mobile')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-2 text-center">
                    <h3
                        className="text-2xl font-semibold text-foreground font-script"
                        style={speakToKitSerifStyle}
                    >
                        Stop guessing. Start targeting the right roles.
                    </h3>
                </div>

                <LandingCta
                    onClick={onCtaClick}
                    className="pt-0 w-full max-w-md self-center"
                    buttonClassName={heroButtonClassName}
                    text={ctaText}
                    showSubtext={false}
                    variant="arrow"
                />
                {signupForm && <div className="w-full">{signupForm}</div>}
            </div>
        </div>
    )
}

function SocialProofCarousel() {
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        const interval = window.setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % testimonials.length)
        }, 5000)

        return () => window.clearInterval(interval)
    }, [])

    return (
        <section className="relative">
            <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
                <svg
                    className="relative block w-full h-12 md:h-16"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                        className="fill-background"
                    />
                </svg>
            </div>

            <div className="bg-gradient-to-b from-primary/5 via-primary/8 to-primary/5 py-12 md:py-14">
                <div
                    className={`${containerClass} max-w-4xl mx-auto relative z-10`}
                >
                    <div className="relative text-center px-4">
                        <span
                            className="absolute -top-6 left-1/2 -translate-x-1/2 text-6xl text-primary/15 font-serif leading-none select-none pointer-events-none"
                            aria-hidden="true"
                        >
                            "
                        </span>

                        <div className="min-h-[140px] flex items-center justify-center">
                            <div className="space-y-3 pt-4">
                                <p className="text-lg md:text-xl text-foreground font-medium leading-relaxed px-2">
                                    “{testimonials[activeIndex].quote}”
                                </p>
                                <div className="text-muted-foreground text-sm">
                                    <span className="font-semibold text-foreground">
                                        {testimonials[activeIndex].author}
                                    </span>
                                    <span className="mx-2">—</span>
                                    <span>
                                        {testimonials[activeIndex].role}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center gap-2 mt-4">
                            {testimonials.map((_, index) => (
                                <button
                                    key={testimonials[index].author}
                                    type="button"
                                    onClick={() => setActiveIndex(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        index === activeIndex
                                            ? 'bg-primary w-6'
                                            : 'bg-primary/30 hover:bg-primary/50'
                                    }`}
                                    aria-label={`Go to testimonial ${
                                        index + 1
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none rotate-180">
                <svg
                    className="relative block w-full h-12 md:h-16"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                        className="fill-background"
                    />
                </svg>
            </div>
        </section>
    )
}

function WhatYouGetSection({
    onPrimaryCtaClick,
    onSeeExample,
    onSeeNetwork,
}: {
    onPrimaryCtaClick: () => void
    onSeeExample: () => void
    onSeeNetwork: () => void
}) {
    return (
        <section className="py-8 md:py-12 bg-muted/30 mt-4">
            <div className={`${containerClass} max-w-5xl`}>
                <div className="text-center mb-6">
                    <h2
                        className="text-3xl md:text-4xl font-bold text-foreground font-script"
                        style={speakToKitSerifStyle}
                    >
                        Two things you&apos;ll walk away with
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                    {whatYouGetCards.map((card) => (
                        <div
                            key={card.title}
                            className="bg-background rounded-2xl border border-border p-6 md:p-8 shadow-sm transition-all duration-200 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1"
                        >
                            <div className="flex items-start gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <card.icon className="w-7 h-7 text-primary" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-semibold text-foreground">
                                            {card.title}
                                        </h3>
                                        {card.badge && (
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground whitespace-nowrap">
                                                {card.badge}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-muted-foreground">
                                        {card.subtitle}
                                    </p>
                                </div>
                            </div>

                            <ul className="space-y-3 mb-6">
                                {card.features.map((feature) => (
                                    <li
                                        key={feature}
                                        className="flex items-start gap-2.5"
                                    >
                                        <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                                        <span className="text-foreground">
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className="border-t border-border pt-4 flex items-center justify-between">
                                <p className="text-sm text-muted-foreground">
                                    {card.footer}
                                </p>
                                <button
                                    type="button"
                                    className="text-sm font-medium px-4 py-1.5 rounded-full border border-border text-foreground hover:border-primary hover:text-primary transition-colors"
                                    onClick={() =>
                                        card.title.includes('Network')
                                            ? onSeeNetwork()
                                            : onSeeExample()
                                    }
                                >
                                    {card.title.includes('Network')
                                        ? 'See Network'
                                        : 'See Example'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-10">
                    <LandingCta
                        onClick={onPrimaryCtaClick}
                        text="Speak to Kit"
                        subtext="8 minutes. Confidential. No obligation."
                        variant="arrow"
                    />
                </div>
            </div>
        </section>
    )
}

function ValuePropositionSection({ onCtaClick }: { onCtaClick: () => void }) {
    return (
        <section className="py-8 md:py-12">
            <div className={containerClass}>
                <div className="text-center mb-6">
                    <h2
                        className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-script"
                        style={speakToKitSerifStyle}
                    >
                        Stop competing with 250 CVs
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Most job seekers waste months applying cold. Here&apos;s
                        what the data actually shows.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
                    <div className="bg-destructive/5 border border-destructive/20 rounded-2xl p-6 md:p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                                <FileText className="w-6 h-6 text-destructive" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground">
                                Applying Cold
                            </h3>
                        </div>

                        <div className="space-y-4 mb-6">
                            {coldStats.map((stat) => (
                                <div
                                    key={stat.value}
                                    className="flex items-start gap-3"
                                >
                                    <X className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                                    <div>
                                        <span className="font-bold text-foreground text-lg">
                                            {stat.value}
                                        </span>
                                        <span className="text-muted-foreground ml-1">
                                            {stat.label}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="text-sm text-muted-foreground italic border-t border-destructive/20 pt-4">
                            &quot;You&apos;re not failing. The system is stacked
                            against you.&quot;
                        </p>
                    </div>

                    <div className="bg-[#E8F8F1] border border-[#B7E3D2] rounded-2xl p-6 md:p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-[#D1F1E1] flex items-center justify-center">
                                <Users className="w-6 h-6 text-[#1A9062]" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground">
                                Applying Through Networks
                            </h3>
                        </div>

                        <div className="space-y-4 mb-6">
                            {networkStats.map((stat) => (
                                <div
                                    key={stat.value}
                                    className="flex items-start gap-3"
                                >
                                    <Check className="w-5 h-5 text-[#1A9062] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <span className="font-bold text-foreground text-lg">
                                            {stat.value}
                                        </span>
                                        <span className="text-muted-foreground ml-1">
                                            {stat.label}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="text-sm text-muted-foreground italic border-t border-[#B7E3D2] pt-4">
                            &quot;Kit finds the connection path. You decide
                            whether to use it.&quot;
                        </p>
                    </div>
                </div>

                <div className="text-center mt-6">
                    <LandingCta
                        onClick={onCtaClick}
                        text="Start your 8-minute call"
                        subtext="Free. Confidential. No CV required."
                        variant="arrow"
                    />
                </div>
            </div>
        </section>
    )
}

function TrustBanner() {
    return (
        <section className="py-12 md:py-16 border-y border-[#FAF7F4]/10 bg-[#2A2118] text-[#FAF7F4]">
            <div className={containerClass}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                    {trustItems.map((item) => (
                        <div key={item.title} className="text-center">
                            <div className="w-12 h-12 rounded-full bg-[#FAF7F4]/10 flex items-center justify-center mx-auto mb-3">
                                <item.icon className="w-5 h-5 text-[#C37141]" />
                            </div>
                            <h4 className="font-semibold text-[#FAF7F4] mb-1">
                                {item.title}
                            </h4>
                            <p className="text-sm text-[#FAF7F4]/80">
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default function CandidateLandingOption2(): React.JSX.Element {
    useSpeakToKitPageTitle()
    const [activeModal, setActiveModal] = useState<ModalType>(null)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
    const formRef = useRef<HTMLDivElement | null>(null)
    const [formFlashTrigger, setFormFlashTrigger] = useState(0)

    useEffect(() => {
        jsCapture('js_jobseeker2_landing_page_viewed')
    }, [])

    useEffect(() => {
        let mounted = true
        const checkAuth = async () => {
            try {
                const res = await authApi.getMeDetails()
                if (!mounted) return
                setIsAuthenticated(Boolean(res.user))
            } catch {
                if (mounted) setIsAuthenticated(false)
            }
        }
        checkAuth()
        return () => {
            mounted = false
        }
    }, [])

    useEffect(() => {
        if (activeModal) {
            document.body.classList.add('overflow-hidden')
        } else {
            document.body.classList.remove('overflow-hidden')
        }

        return () => {
            document.body.classList.remove('overflow-hidden')
        }
    }, [activeModal])

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }

    const showSignup = isAuthenticated === false
    const heroCtaText = 'Speak to Kit'

    const heroLayoutClass =
        'flex flex-col items-center justify-center gap-8 font-sans'
    const voiceCardWrapperClass = 'w-full max-w-3xl mx-auto'

    const signupForm = showSignup ? (
        <div className="w-full" ref={formRef}>
            <div className="mx-auto w-full max-w-2xl">
                <CandidateSignUpForm
                    className="h-full"
                    formClassName="h-full"
                    mode="embedded"
                    flashTrigger={formFlashTrigger}
                />
            </div>
        </div>
    ) : null

    const handleFormCtaClick = () => {
        ensureJsAttemptId()
        jsCapture('js_jobseeker2_form_cta_clicked')
        if (isAuthenticated) {
            window.location.href = Pages.CandidateInterview
            return
        }

        if (showSignup) {
            scrollToForm()
            setFormFlashTrigger((prev) => prev + 1)
        }
    }

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={speakToKitThemeVars}
        >
            <header className="w-full pt-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    <SpeakToKitLogo to={Pages.CandidateLandingOption2} />
                </div>
            </header>
            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={`${containerClass} max-w-6xl`}>
                        <div className={heroLayoutClass}>
                            <div className={voiceCardWrapperClass}>
                                <VoiceCard
                                    onCtaClick={handleFormCtaClick}
                                    ctaText={heroCtaText}
                                    signupForm={signupForm}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <SocialProofCarousel />

                <WhatYouGetSection
                    onPrimaryCtaClick={handleFormCtaClick}
                    onSeeExample={() => setActiveModal('profile')}
                    onSeeNetwork={() => setActiveModal('network')}
                />

                <ValuePropositionSection onCtaClick={handleFormCtaClick} />

                <TrustBanner />
            </main>

            <footer className="py-8 border-t border-border">
                <div className={containerClass}>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                        <a
                            href="https://contxt.network/privacy-policy"
                            className="hover:text-foreground transition-colors"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Privacy
                        </a>
                        <a
                            href="https://contxt.network/terms"
                            className="hover:text-foreground transition-colors"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Terms
                        </a>
                        <a
                            href="https://contxt.network/contact"
                            className="hover:text-foreground transition-colors"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Contact
                        </a>
                    </div>
                    <div className="text-center mt-6">
                        <LandingCta
                            onClick={handleFormCtaClick}
                            text="Speak to Kit"
                            showSubtext={false}
                            variant="arrow"
                        />
                    </div>
                </div>
            </footer>

            {activeModal && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4 py-8">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between border-b border-border px-6 py-4">
                            <h3 className="text-lg font-semibold text-foreground">
                                {activeModal === 'profile'
                                    ? 'Example Team Match Profile'
                                    : 'Network Preview'}
                            </h3>
                            <button
                                type="button"
                                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                onClick={() => setActiveModal(null)}
                            >
                                Close
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-6 bg-muted/30">
                            {activeModal === 'profile' ? (
                                <iframe
                                    src={PROFILE_PREVIEW_URL}
                                    title="Profile preview"
                                    className="w-full h-[65vh] rounded-xl border border-border bg-white"
                                />
                            ) : (
                                <img
                                    src={NETWORK_PREVIEW_IMG}
                                    alt="Network preview"
                                    className="w-full rounded-xl border border-border bg-white object-contain"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
